import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { apiService } from '../services/apiService';

function formatFeatureTokenUsage(u: any): string {
  const total = u?.featureTokenTotal ?? 0;
  const calls = u?.featureTokenApiCalls ?? 0;
  if (total === 0 && calls === 0) return '—';
  return `${Number(total).toLocaleString()} tokens · ${calls} LLM call${calls === 1 ? '' : 's'}`;
}

function featureTokenTitle(u: any): string | undefined {
  const p = u?.featureTokenPrompt ?? 0;
  const c = u?.featureTokenCompletion ?? 0;
  const t = u?.featureTokenTotal ?? 0;
  if (t === 0 && (u?.featureTokenApiCalls ?? 0) === 0) return undefined;
  return `Prompt: ${Number(p).toLocaleString()} · Completion: ${Number(c).toLocaleString()} · Total: ${Number(t).toLocaleString()}`;
}

const defaultForm = {
  firstName: '',
  lastName: '',
  email: '',
  role: 'MEMBER',
  status: 'ACTIVE',
  tenantId: '',
  password: '',
};

type PendingActionType = 'delete' | 'activate' | 'deactivate' | 'force-logout' | null;

function resolveUserId(u: any): number | null {
  const raw = u?.userId ?? u?.id ?? u?.user_id;
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

const SuperAdminUserManagementPage: React.FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [roles, setRoles] = useState<any[]>([]);

  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState<any>(defaultForm);
  const [editUserId, setEditUserId] = useState<number | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [pendingAction, setPendingAction] = useState<{ type: PendingActionType; user: any | null }>({ type: null, user: null });

  const query = useMemo(() => ({ search: search.trim() || undefined }), [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [users, roleOptions] = await Promise.all([
        apiService.getAdminUsers(query),
        apiService.getAdminUserRoles(),
      ]);
      setRows(users || []);
      setRoles(roleOptions || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const submitCreate = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload: any = { ...form, tenantId: form.tenantId ? Number(form.tenantId) : undefined };
      await apiService.createAdminUser(payload);
      setOpenCreate(false);
      setForm(defaultForm);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (u: any) => {
    const uid = resolveUserId(u);
    if (!uid) {
      setError('Unable to identify selected user. Please reload and try again.');
      return;
    }
    setEditUserId(uid);
    setForm({
      firstName: (u.fullName || '').split(' ').slice(0, -1).join(' ') || u.fullName || '',
      lastName: (u.fullName || '').split(' ').slice(-1).join(' ') || '',
      email: u.email || '',
      role: u.role || 'MEMBER',
      status: u.status || 'ACTIVE',
      tenantId: u.tenantId != null ? String(u.tenantId) : '',
      password: '',
    });
  };

  const submitEdit = async () => {
    if (!editUserId) return;
    setLoading(true);
    setError(null);
    try {
      const payload: any = { ...form, tenantId: form.tenantId ? Number(form.tenantId) : undefined };
      delete payload.password;
      await apiService.updateAdminUser(editUserId, payload);
      setEditUserId(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const resetPass = async () => {
    if (!editUserId || !resetPassword.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await apiService.resetAdminUserPassword(editUserId, resetPassword.trim());
      setResetPassword('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const action = async (fn: () => Promise<any>) => {
    setLoading(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  const runPendingAction = async () => {
    const type = pendingAction.type;
    const user = pendingAction.user;
    const uid = resolveUserId(user);
    if (!type || !uid) {
      setPendingAction({ type: null, user: null });
      return;
    }
    const map: Record<Exclude<PendingActionType, null>, () => Promise<any>> = {
      delete: () => apiService.deleteAdminUser(uid),
      activate: () => apiService.activateAdminUser(uid),
      deactivate: () => apiService.deactivateAdminUser(uid),
      'force-logout': () => apiService.forceLogoutAdminUser(uid),
    };
    await action(map[type]);
    setPendingAction({ type: null, user: null });
  };

  const pendingActionLabel = useMemo(() => {
    switch (pendingAction.type) {
      case 'delete': return 'Delete user';
      case 'activate': return 'Activate user';
      case 'deactivate': return 'Deactivate user';
      case 'force-logout': return 'Force logout user';
      default: return '';
    }
  }, [pendingAction.type]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PeopleAltIcon />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>User Management</Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField size="small" label="Search user" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Button variant="contained" onClick={() => setOpenCreate(true)} disabled={loading}>Create User</Button>
          <Button variant="outlined" onClick={load} disabled={loading}>Reload</Button>
        </Box>
      </Paper>

      {loading && <CircularProgress size={22} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Feature token usage</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Device</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Projects</TableCell>
              <TableCell>Environments</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((u, index) => (
              <TableRow key={u.userId}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{u.fullName}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.role || '-'}</TableCell>
                <TableCell>{u.status}</TableCell>
                <TableCell>{u.tenantId ?? '-'}</TableCell>
                <TableCell title={featureTokenTitle(u)} sx={{ maxWidth: 200, whiteSpace: 'normal' }}>
                  {formatFeatureTokenUsage(u)}
                </TableCell>
                <TableCell>{u.lastLoginTime || '-'}</TableCell>
                <TableCell>{u.ipAddress || '-'}</TableCell>
                <TableCell>{u.osName || '-'} / {u.browserName || '-'}</TableCell>
                <TableCell>{u.accountCreatedDate || '-'}</TableCell>
                <TableCell>{(u.associatedProjects || []).slice(0, 2).join(', ') || '-'}</TableCell>
                <TableCell>{(u.assignedEnvironments || []).slice(0, 2).join(', ') || '-'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Button size="small" variant="text" onClick={() => openEditDialog(u)}>Edit</Button>
                    <Chip
                      size="small"
                      color="error"
                      label="Delete"
                      onClick={() => setPendingAction({ type: 'delete', user: u })}
                    />
                    {u.status === 'ACTIVE' ? (
                      <Chip
                        size="small"
                        label="Deactivate"
                        onClick={() => setPendingAction({ type: 'deactivate', user: u })}
                      />
                    ) : (
                      <Chip
                        size="small"
                        color="success"
                        label="Activate"
                        onClick={() => setPendingAction({ type: 'activate', user: u })}
                      />
                    )}
                    <Chip
                      size="small"
                      label="Force Logout"
                      onClick={() => setPendingAction({ type: 'force-logout', user: u })}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        open={!!pendingAction.type}
        onClose={() => setPendingAction({ type: null, user: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{pendingActionLabel}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Confirm action for{' '}
            <strong>{pendingAction.user?.email || pendingAction.user?.fullName || 'selected user'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAction({ type: null, user: null })}>Cancel</Button>
          <Button variant="contained" color={pendingAction.type === 'delete' ? 'error' : 'primary'} onClick={runPendingAction}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create User</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          <TextField label="First Name" value={form.firstName} onChange={(e) => setForm((p: any) => ({ ...p, firstName: e.target.value }))} />
          <TextField label="Last Name" value={form.lastName} onChange={(e) => setForm((p: any) => ({ ...p, lastName: e.target.value }))} />
          <TextField label="Email" value={form.email} onChange={(e) => setForm((p: any) => ({ ...p, email: e.target.value }))} />
          <TextField label="Tenant ID (organization)" value={form.tenantId} onChange={(e) => setForm((p: any) => ({ ...p, tenantId: e.target.value }))} />
          <TextField select label="Role" value={form.role} onChange={(e) => setForm((p: any) => ({ ...p, role: e.target.value }))}>
            {roles.map((r) => <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>)}
          </TextField>
          <TextField select label="Status" value={form.status} onChange={(e) => setForm((p: any) => ({ ...p, status: e.target.value }))}>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Disabled</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="SUSPENDED">Suspended</MenuItem>
          </TextField>
          <TextField type="password" label="Password" value={form.password} onChange={(e) => setForm((p: any) => ({ ...p, password: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editUserId} onClose={() => setEditUserId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          <TextField label="First Name" value={form.firstName} onChange={(e) => setForm((p: any) => ({ ...p, firstName: e.target.value }))} />
          <TextField label="Last Name" value={form.lastName} onChange={(e) => setForm((p: any) => ({ ...p, lastName: e.target.value }))} />
          <TextField label="Email" value={form.email} onChange={(e) => setForm((p: any) => ({ ...p, email: e.target.value }))} />
          <TextField label="Tenant ID (organization)" value={form.tenantId} onChange={(e) => setForm((p: any) => ({ ...p, tenantId: e.target.value }))} />
          <TextField select label="Role" value={form.role} onChange={(e) => setForm((p: any) => ({ ...p, role: e.target.value }))}>
            {roles.map((r) => <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>)}
          </TextField>
          <TextField select label="Status" value={form.status} onChange={(e) => setForm((p: any) => ({ ...p, status: e.target.value }))}>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Disabled</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="SUSPENDED">Suspended</MenuItem>
          </TextField>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              type="password"
              label="Reset password"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
            />
            <Button onClick={resetPass} variant="outlined">Reset</Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserId(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitEdit}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminUserManagementPage;

